import * as React from "react";
import { Modal, ModalHeader, ModalBody } from "reactstrap";
import { withRouter, RouteComponentProps } from "react-router-dom";

import { HorseListStore } from "../../stores/HorseListStore";
import { ConfigurationStore } from "../../stores/ConfigurationStore";

import { Horse } from "src/model/Horse";

import { HorseLink } from "../common/HorseLink";
import LoadingWidget from "../common/LoadingWidget";
import { successMessage, errorMessage } from "../common/Toast";

const NONE_ID: number = -1;

export interface Props extends RouteComponentProps<any> {
    horseListStore: HorseListStore;
    configurationStore: ConfigurationStore;
    show: boolean;
    onClose?: () => void;
    onCreate?: (horse: Horse) => void;
    noRedirect?: boolean;
}

export interface State {
    isLoading: boolean;
    wasValidated: boolean;
    isWarning: boolean;
    name: string;
    gaitId: number;
}

export class NewHorseDialog extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            isLoading: false,
            wasValidated: false,
            isWarning: false,
            name: "",
            gaitId: NONE_ID
        };
    }

    handleOnChange = (event: React.FormEvent<HTMLInputElement>): void => {
        const target = event.currentTarget;
        const value = target.value;
        const name = target.name;

        const state = this.state;
        state[name] = value;
        this.setState(state);

        if (this.state.name !== "" && value.length > 3) {
            this.props.horseListStore!.refreshSimilarHorses(this.state.name);
            this.setState({ isWarning: true });
        } else {
            this.setState({ isWarning: false });
        }
    };

    resetDialog = () => {
        this.setState({
            wasValidated: false,
            isWarning: false,
            name: "",
            gaitId: NONE_ID
        });
    };

    handleOnChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>): void => {
        e.preventDefault();
        const gaitId = parseInt(e.target.value, 10);
        this.setState({ gaitId });
    };

    createNewHorse = async () => {
        try {
            const horse = await this.props.horseListStore!.addHorse({
                name: this.state.name,
                gaitId: this.state.gaitId !== NONE_ID ? this.state.gaitId : undefined
            });
            successMessage(`${this.state.name} has been created.`);

            if (this.props.onCreate) {
                this.props.onCreate(horse);
            }
            if (!this.props.noRedirect) {
                this.props.history!.push(`/horse/${horse.id}/`);
            } else {
                this.toggle();
            }
        } catch (error) {
            errorMessage(error);
        }

        this.setState({ isLoading: false });
    };

    submit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        e.stopPropagation();

        if (this.formValidation()) {
            this.setState({ wasValidated: false, isLoading: true });
            this.createNewHorse();
        } else {
            this.setState({ wasValidated: true });
        }
    };

    formValidation = () => {
        return this.state.name !== "" ? true : false;
    };

    toggle = () => {
        if (this.props.show && this.props.onClose) {
            this.props.onClose();
        }
    };

    componentDidUpdate(prevProps: Props, prevState: State) {
        if (prevProps.show !== this.props.show) {
            this.resetDialog();
        }
    }

    render() {
        const horseListStore = this.props.horseListStore!;
        const similar = horseListStore.similarHorses.results;

        return (
            <div>
                <Modal isOpen={this.props.show} toggle={this.toggle} className="modal-md" centered={true}>
                    <ModalHeader toggle={this.toggle}>New Horse</ModalHeader>
                    <ModalBody>
                        <form
                            className={"mt-3 needs-validation " + (this.state.wasValidated === true ? "was-validated" : "")}
                            noValidate={true}
                            onSubmit={e => this.submit(e)}
                        >
                            <div className="form-group">
                                <label htmlFor="name">
                                    Horse Name <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="name"
                                    placeholder="Horse name"
                                    name="name"
                                    maxLength={18}
                                    value={this.state.name}
                                    onChange={this.handleOnChange}
                                    required={true}
                                    disabled={this.state.isLoading}
                                />
                                <small id="registrationHelpBlock" className="form-text text-muted">
                                    Horse names can only be 18 characters long, including spaces.
                                </small>
                            </div>
                            <div className="form-group">
                                <label htmlFor="gait">Gait</label>
                                <select
                                    className="form-control not-required"
                                    name="gait"
                                    disabled={this.state.isLoading}
                                    onChange={this.handleOnChangeSelect}
                                    value={this.state.gaitId}
                                >
                                    <option value={NONE_ID}> - Unknown - </option>
                                    {this.props.configurationStore.horsesGaits.map(g => (
                                        <option key={g.id} value={g.id}>
                                            {g.name} ({g.abbreviation})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {this.state.isWarning && similar.length > 0 && (
                                <div className="alert alert-warning" role="alert">
                                    <b> Warning: </b>The following horse(s) with similar names already exist. Please check these before continuing to
                                    avoid duplicates:
                                    <ul className="pl-4 mb-0 mt-2">
                                        {similar.map((horse, index) => (
                                            <li key={index}>
                                                <HorseLink horse={horse} className={"d-block"} targetBlank={true} />
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <div className="modal-footer px-0">
                                <button className="btn btn-secondary" type="button" onClick={this.toggle}>
                                    Cancel
                                </button>
                                {this.state.isLoading && <LoadingWidget />}
                                <button className="btn btn-primary ml-auto" type="submit">
                                    Create Horse
                                </button>
                            </div>
                        </form>
                    </ModalBody>
                </Modal>
            </div>
        );
    }
}

export default withRouter<Props>(NewHorseDialog);



// WEBPACK FOOTER //
// ./src/components/horses/NewHorseDialog.tsx